<template>
    <form class="dynamic-table-relation-form">
        <div class="iform-row">
            <div class="main-data-container">
                <div class="d-flex justify-content-between mb-2">
                    <app-select
                        label="Connection type"
                        :options="getConnectionTypesList()"
                        :is-multiple="false"
                        :use-search="false"
                        :is-required="true"
                        v-model="connectionTypeId"
                    ></app-select>
                    <app-select
                        label="Path type"
                        :options="getPathTypesList()"
                        :is-multiple="false"
                        :use-search="false"
                        :is-required="true"
                        v-model="pathType"
                    ></app-select>
                </div>
                <div class="d-flex justify-content-between mb-2">
                    <app-input
                        label="Source table"
                        :is-required="true"
                        placeholder="users"
                        v-model="sourceTable"
                    ></app-input>
                    <app-input
                        label="Destination table"
                        :is-required="true"
                        placeholder="courses"
                        v-model="destinationTable"
                    ></app-input>
                </div>

                <div class="d-flex justify-content-between mb-2">
                    <app-select
                        label="Company"
                        :options="getCompaniesList()"
                        :is-multiple="false"
                        :use-search="true"
                        :is-required="false"
                        v-model="companyId"
                    ></app-select>
                    <app-select
                        label="LMS Connection"
                        :options="lmsConnections"
                        :is-multiple="false"
                        :use-search="true"
                        :is-required="false"
                        v-model="lmsConnectionId"
                        :key="JSON.stringify(lmsConnections)"
                    ></app-select>
                </div>
            </div>

            <h5>Steps</h5>

            <div class="steps-container">
                <table class="table table-bordered">
                    <tr>
                        <th>Relating table</th>
                        <th>Related table</th>
                        <th>Step type</th>
                        <th></th>
                    </tr>
                    <template v-if="pathSteps.length">
                        <tr v-for="step in pathSteps">
                            <td>{{ step.relatingTable }}</td>
                            <td>{{ step.relatedTable }}</td>
                            <td>{{ getStepTypeTitle(step.type) }}</td>
                            <td>
                                <app-button @click="editStep(step.id)" type="warning">Edit</app-button>
                                <app-button @click="deleteStep(step.id)" type="error">Delete</app-button>
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr>
                            <td colspan="4" class="text-center">No steps</td>
                        </tr>
                    </template>
                </table>
                <hr>
                <step-builder
                    :init-data="stepEditorData"
                    :step-types="stepTypes"
                    :filter-operators="filterOperators"
                    @add-step="addStep"
                    :key="JSON.stringify(stepEditorData)"
                ></step-builder>
            </div>
        </div>
        <button-set>
            <app-button
                type="primary"
                :prevent-default="true"
                @click="saveRelation"
                :class="{disabled: !relationIsReady}"
            >
                <ion-icon name="checkmark-done-outline"></ion-icon>
                Save
            </app-button>
            <app-button :link="route('report-builder.dynamic-tables.relations.index')">
                Cancel
            </app-button>
        </button-set>
        <loading-overlay :loading="loading"></loading-overlay>
    </form>
</template>

<script>
    import StepBuilder from "./StepBuilder";

    export default {
        props: {
            actionUrl: {
                type: String,
                required: true,
            },
            connectionTypes: {
                type: Array,
                required: true,
            },
            pathTypes: {
                type: Array,
                required: true,
            },
            stepTypes: {
                type: Array,
                required: true,
            },
            filterOperators: {
                type: Array,
                required: true,
            },
            initRelationData: {
                type: Object,
                required: true,
            },
            companies: {
                type: Array,
                default: () => ([]),
            },
        },
        data() {
            return {
                connectionTypeId: this.initRelationData.connection_type_id || '',
                sourceTable: this.initRelationData.source_table || '',
                destinationTable: this.initRelationData.destination_table || '',
                pathType: this.initRelationData.path_type || '',
                pathSteps: this.initRelationData.path_steps || [],
                companyId: this.initRelationData.lms_connection?.company_id || 0,
                lmsConnectionId: this.initRelationData.lms_connection?.id || 0,
                lmsConnections: [],
                stepEditorData: {},
                loading: false,
            };
        },
        watch: {
            companyId() {
                this.loadLmsConnections();
            },
        },
        mounted() {
            if (this.lmsConnectionId) {
                this.loadLmsConnections(this.lmsConnectionId);
            }
        },
        computed: {
            relationIsReady() {
                return this.pathType
                    && this.connectionTypeId
                    && this.sourceTable
                    && this.destinationTable
                    && this.pathType
                    && this.pathSteps.length;
            },
        },
        components: {
            StepBuilder,
        },
        methods: {
            getStepTypeTitle(stepTypeId) {
                return this.stepTypes.find(type => type.id === stepTypeId).title;
            },
            addStep(step) {
                const existingStep = this.pathSteps.find(existingStep => existingStep.id === step.id);

                if (existingStep) {
                    for (let i = 0; i < this.pathSteps.length; i++) {
                        if (this.pathSteps[i].id === step.id) {
                            this.$set(this.pathSteps, i, step);
                            break;
                        }
                    }
                } else {
                    this.pathSteps.push(step);
                }

                this.stepEditorData = {};
            },
            editStep(stepId) {
                const step = this.pathSteps.find(step => step.id === stepId);
                this.stepEditorData = {
                    filters: step.filters,
                    relatingTable: step.relatingTable,
                    relatedTable: step.relatedTable,
                    type: step.type,
                    id: step.id,
                };
            },
            deleteStep(stepId) {
                this.pathSteps = this.pathSteps.filter(step => step.id !== stepId);
            },
            saveRelation() {
                if (!this.relationIsReady) {
                    return;
                }

                this.loading = true;
                let savePromise;
                const data = {
                    connection_type_id: this.connectionTypeId,
                    path_type: this.pathType,
                    source_table: this.sourceTable,
                    destination_table: this.destinationTable,
                    path_steps: this.pathSteps,
                    lms_connection_id: this.lmsConnectionId || null,
                }

                if (this.initRelationData.id) {
                    savePromise = axios.post(this.actionUrl, data);
                } else {
                    savePromise = axios.put(this.actionUrl, data);
                }

                savePromise.then(response => {
                    window.location.href = this.route('report-builder.dynamic-tables.relations.index');
                }).catch(() => {
                    this.loading = false;
                });
            },
            getConnectionTypesList() {
                return copy(this.connectionTypes);
            },
            getCompaniesList() {
                return copy(this.companies);
            },
            getPathTypesList() {
                return copy(this.pathTypes);
            },
            loadLmsConnections(selectedLmsConnectionId) {
                this.lmsConnections = [];
                this.lmsConnectionId = selectedLmsConnectionId || 0;
                this.loading = true;

                axios.get(
                    this.route('companies.lms-connections', {company: this.companyId})
                ).then(response => {
                    this.lmsConnections = response.data;
                    this.loading = false;
                });
            }
        },
    };
</script>
